import type {
  GridItemHTMLElement,
  GridStack,
  GridStackOptions,
  GridStackWidget,
} from "gridstack";
import { type PropsWithChildren, useCallback, useRef, useState } from "react";
import { GridStackContext } from "./grid-stack-context";
import { gridWidgetContainersMap } from "./grid-stack-render-provider";

export function GridStackProvider({
  children,
  initialOptions,
}: PropsWithChildren<{ initialOptions: GridStackOptions }>) {
  const [gridStack, setGridStack] = useState<GridStack | null>(null);
  const [rawWidgetMetaMap, setRawWidgetMetaMap] = useState(() => {
    const map = new Map<string, GridStackWidget>();
    const deepFindNodeWithContent = (obj: GridStackWidget) => {
      if (obj.id && obj.content) {
        map.set(obj.id, obj);
      }
      if (obj.subGridOpts?.children) {
        obj.subGridOpts.children.forEach((child: GridStackWidget) => {
          deepFindNodeWithContent(child);
        });
      }
    };
    initialOptions.children?.forEach((child: GridStackWidget) => {
      deepFindNodeWithContent(child);
    });
    return map;
  });

  // Miroir synchrone de la metaMap : sert de source de vérité immédiate (avant
  // le re-render React) pour lire le `content` à jour lors de saveOptions().
  const metaMapRef = useRef(rawWidgetMetaMap);

  const commitMetaMap = useCallback(
    (updater: (prev: Map<string, GridStackWidget>) => Map<string, GridStackWidget>) => {
      const next = updater(metaMapRef.current);
      metaMapRef.current = next;
      setRawWidgetMetaMap(next);
    },
    []
  );

  const addWidget = useCallback(
    (widget: GridStackWidget & { id: Required<GridStackWidget>["id"] }) => {
      if (!gridStack) return;

      gridStack.addWidget(widget);
      commitMetaMap((prev) => {
        const newMap = new Map<string, GridStackWidget>(prev);
        newMap.set(widget.id, widget);
        return newMap;
      });
    },
    [gridStack, commitMetaMap]
  );

  const addSubGrid = useCallback(
    (
      subGrid: GridStackWidget & {
        id: Required<GridStackWidget>["id"];
        subGridOpts: Required<GridStackWidget>["subGridOpts"] & {
          children: Array<
            GridStackWidget & { id: Required<GridStackWidget>["id"] }
          >;
        };
      }
    ) => {
      gridStack?.addWidget(subGrid);

      commitMetaMap((prev) => {
        const newMap = new Map<string, GridStackWidget>(prev);
        subGrid.subGridOpts?.children?.forEach(
          (meta: GridStackWidget & { id: Required<GridStackWidget>["id"] }) => {
            newMap.set(meta.id, meta);
          }
        );
        return newMap;
      });
    },
    [gridStack, commitMetaMap]
  );

  const removeWidget = useCallback(
    (id: string) => {
      if (!gridStack) return;

      const element = gridStack.el?.querySelector<GridItemHTMLElement>(
        `[gs-id="${id}"]`
      );
      if (element) {
        gridStack.removeWidget(element);
      }

      gridWidgetContainersMap.get(gridStack)?.delete(id);

      commitMetaMap((prev) => {
        const newMap = new Map<string, GridStackWidget>(prev);
        newMap.delete(id);
        return newMap;
      });
    },
    [gridStack, commitMetaMap]
  );

  const updateWidget = useCallback(
    (id: string, widget: Partial<GridStackWidget> & { content?: string }) => {
      if (!gridStack) return;

      const element = gridStack.el?.querySelector<GridItemHTMLElement>(
        `[gs-id="${id}"]`
      );
      const currentMeta = metaMapRef.current.get(id);
      if (!currentMeta) return;

      const updatedMeta: GridStackWidget = {
        ...currentMeta,
        ...widget,
        id,
      };

      if (element) {
        gridStack.update(element, {
          w: updatedMeta.w,
          h: updatedMeta.h,
        });
      }

      commitMetaMap((prev) => {
        const newMap = new Map<string, GridStackWidget>(prev);
        newMap.set(id, updatedMeta);
        return newMap;
      });
    },
    [gridStack, commitMetaMap]
  );

  const saveOptions = useCallback(() => {
    // saveCB fourni → GridStack n'écrase pas content depuis le DOM ; on injecte
    // le content à jour depuis la metaMap (le DOM/engine peut être en retard
    // après un updateWidget qui ne change que les props, ex. ville météo).
    return gridStack?.save(true, true, (_, widget) => {
      const id = widget.id as string | undefined;
      const meta = id ? metaMapRef.current.get(id) : undefined;
      if (meta?.content !== undefined) widget.content = meta.content;
      return widget;
    });
  }, [gridStack]);

  const removeAll = useCallback(() => {
    gridStack?.removeAll();
    metaMapRef.current = new Map<string, GridStackWidget>();
    setRawWidgetMetaMap(new Map<string, GridStackWidget>());
  }, [gridStack]);

  return (
    <GridStackContext.Provider
      value={{
        initialOptions,
        gridStack,

        addWidget,
        removeWidget,
        updateWidget,
        addSubGrid,
        saveOptions,
        removeAll,

        _gridStack: {
          value: gridStack,
          set: setGridStack,
        },
        _rawWidgetMetaMap: {
          value: rawWidgetMetaMap,
          set: setRawWidgetMetaMap,
        },
      }}
    >
      {children}
    </GridStackContext.Provider>
  );
}
